import { Box, Grid, Hidden, Typography } from '@mui/material'
import { styled, Theme } from '@mui/material/styles'
import MultiAccountBadge from 'components/badges/MultiAccountBadge'
import Button from 'components/buttons/Button'
import IconButton from 'components/buttons/IconButton'
import Card from 'components/cards/Card'
import Select from 'components/form/Select'

import Avatar from '../badges/Avatar'
import { APP_COLOR } from 'utils/colors'
import i18n from 'i18n'
import { isMobileDevice } from 'utils/constants'
import {
  monthViewIcon,
  dayViewIcon,
  weekViewIcon,
  listViewIcon,
  oneDayListIcon
} from 'assets/img'
import { isGuest } from 'utils/user'

// Define your screen size variable
export const screenLgMin = isGuest() ? '951px' : '1301px'

export const Calendar = styled(Card)(() => ({
  flex: 1,
  width: '100%',
  height: '100%',
  display: 'flex',
  overflow: 'hidden',
  flexDirection: 'column'
}))

export const CalendarHeader = styled(Box)<{
  isMobile?: boolean
  isCondensedHeight?: boolean
}>(({ theme, isMobile, isCondensedHeight }) => ({
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'center',
  justifyContent: 'space-between',
  flexDirection: isMobile ? 'column' : 'row',
  width: '100%',
  padding: isCondensedHeight
    ? '0.5rem 1rem'
    : theme.spacing(isMobile ? 2 : 4, isMobile ? 2 : 5),
  borderBottom: '1px solid',
  borderColor: theme.palette.border.primary,
  overflowX: 'clip'
}))

export const CalendarHeaderInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 0',
  minWidth: 60,
  width: 0,
  overflow: 'hidden',
  '@media (max-width: 900px)': {
    width: '100%',
    flex: '0 1 auto',
    minWidth: 'auto',
    overflow: 'visible'
  }
}))

export const CalendarHeaderTitleContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden'
}))

interface CalendarHeaderTitleProps {
  theme?: Theme
  fontSize?: string
}

export const CalendarHeaderTitle = styled('div')(
  ({ theme, fontSize }: CalendarHeaderTitleProps) => ({
    fontSize: fontSize ?? '1.625rem',
    fontWeight: 500,
    width: '100%',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    overflow: 'hidden',
    cursor: 'pointer',
    color: theme?.palette.text.primary,
    minHeight: '24px'
  })
)

export const CalendarHeaderSubtitle = styled('span')(({ theme, color }) => ({
  fontSize: '0.9rem',
  fontWeight: 400,
  width: '100%',
  display: 'block',
  color: color ?? theme.palette.input.placeholder,
  cursor: 'pointer',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  '&:hover': {
    textDecoration: 'underline',
    textDecorationColor: theme.palette.border.primary,
    textUnderlineOffset: '2px'
  }
}))

export const CalendarHeaderActions = styled(Box)<{ isMobile?: boolean }>(
  ({ theme, isMobile }) => ({
    display: 'flex',
    alignItems: 'center',
    zIndex: 20,
    flexShrink: 0,
    ...(isMobile
      ? {
          width: '100%',
          justifyContent: 'flex-end'
        }
      : {
          width: 'fit-content',
          background: theme.palette.card.primary
        })
  })
)

export const CalendarHeaderActionsCell = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content'
}))

export const Separator = styled('div')<{ isMobile?: boolean }>(
  ({ theme, isMobile }) => ({
    width: 1,
    height: 30,
    margin: isMobile ? '0.5rem' : '1rem',
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.28)'
        : theme.palette.border.primary,
    flexShrink: 0
  })
)

interface RangeSelectProps {
  showIcon?: boolean
  customIcon?: string | typeof monthViewIcon
}

export const RangeSelect = styled(Select, {
  shouldForwardProp: (prop) =>
    !['showIcon', 'customIcon'].includes(prop as string)
})<RangeSelectProps>(({ theme, showIcon = true, customIcon }) => ({
  width: 'auto',
  color: theme.palette.text.primary,
  '& > div': {
    width: 'auto'
  },
  '[class*="-singleValue"]': {
    display: showIcon ? 'none' : 'inherit',
    maxWidth: 'none',
    ...(showIcon && {
      [`@media (min-width: ${screenLgMin})`]: {
        display: 'inherit'
      },
      '@media (max-height: 500px)': {
        display: 'none'
      }
    })
  },
  '[class*="-control"]': {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    background: theme.palette.card.primary,
    '&:hover': {
      background: theme.palette.surface.hover
    },
    '&:active, &:focus': {
      background: 'rgba(15, 127, 235, 0.1)'
    },
    '& > div:first-of-type': {
      position: showIcon ? 'relative' : 'inherit',
      padding: '1px 0.5rem',
      ...(showIcon && {
        width: '35px',
        justifyContent: 'center',
        [`@media (min-width: ${screenLgMin})`]: {
          position: 'inherit',
          width: 'auto',
          padding: '1px 0.5rem'
        },
        '@media (max-height: 500px)': {
          width: '35px',
          justifyContent: 'center'
        }
      }),
      ...(showIcon && {
        '&::before': {
          background: `url(${customIcon || monthViewIcon}) no-repeat center`,
          backgroundSize: 'contain',
          content: '""',
          display: 'inline-block',
          height: '20px',
          marginTop: '4px',
          width: '20px',
          filter:
            theme.palette.mode === 'dark'
              ? 'brightness(0) saturate(100%) invert(100%)'
              : 'none',

          [`@media (min-width: ${screenLgMin})`]: {
            display: 'none'
          },
          '@media (max-height: 500px)': {
            display: 'inline-block !important'
          }
        }
      })
    },
    '[class*="-IndicatorsContainer"]': showIcon
      ? {
          display: 'flex',
          [`@media (min-width: ${screenLgMin})`]: {
            display: 'inherit'
          },
          '@media (max-height: 500px)': {
            display: 'flex'
          },
          '& svg': {
            color: theme.palette.text.secondary,
            width: '12px',
            height: '12px',
            marginTop: '4px'
          }
        }
      : {
          display: 'inherit'
        },
    '.filter__indicators': showIcon
      ? {
          '@media (max-width: 900px)': {
            display: 'none'
          },
          '@media (max-height: 500px)': {
            display: 'none'
          }
        }
      : {}
  },
  ...(showIcon &&
    !customIcon && {
      '&.CALENDAR_RANGE\\/DAY': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${dayViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/WEEK': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${weekViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/LISTWEEK': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${listViewIcon}) no-repeat`
        }
      },
      '&.CALENDAR_RANGE\\/LISTDAY': {
        '[class*="-control"] > div:first-of-type::before': {
          background: `url(${oneDayListIcon}) no-repeat`
        }
      }
    })
}))

export const IconButtonSlide = styled(IconButton)(() => ({
  '&[data-prev]': {
    '& svg': {
      transform: 'rotate(90deg) translateY(1px)'
    }
  },
  '&[data-next]': {
    '& svg': {
      transform: 'rotate(-90deg) translateY(1px)'
    }
  }
}))

export const AllAccountBadge = styled(MultiAccountBadge)(({ theme }) => ({
  padding: theme.spacing(0, 3, 0, 0)
}))

export const LocationLabel = styled('div')(({ theme }) => ({
  fontWeight: 300,
  fontSize: 15
}))

export const AvatarBadge = styled(Avatar)(({ theme }) => ({}))

export const CalendarView = styled(Grid)(() => ({
  flex: 1,
  height: '100%',
  position: 'relative'
}))

export const CalendarViewScrollContainer = styled(Grid)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%'
}))

export const CalendarViewScroll = styled(Grid)(() => ({
  width: '100%',
  height: '100%',
  '&[data-calendar="CALENDAR_RANGE/CARDS"] .fc': {
    visibility: 'hidden'
  }
}))

export const AddEventButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  bottom: 20,
  left: i18n.dir() === 'ltr' ? 20 : undefined,
  right: i18n.dir() === 'ltr' ? undefined : 20,
  zIndex: 1001,

  '& svg': {
    width: 32,
    height: 32,
    marginRight: 0
  }
}))

export const ListViewScrollable = styled('div')(({ theme }) => ({
  overflow: 'auto',
  height: '100%',
  zIndex: 1,
  backgroundColor: theme.palette.background.default
}))

export const DateHeader = styled('div')<{ isFirst: boolean; isToday: boolean }>(
  ({ theme, isFirst, isToday }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    height: 40,
    width: '100%',
    padding: theme.spacing(0, 4),
    borderBottom: `1px solid ${theme.palette.border.primary}`,
    borderTop: `1px solid ${theme.palette.border.primary}`,
    background: isToday
      ? APP_COLOR
      : 'var(--calendar-date-header-gradient, linear-gradient(119deg, #F9F9F9 10.89%, #EFEEEE 97.51%))',
    borderColor: theme.palette.border.primary
  })
)

export const EventRow = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  flexDirection: 'row',
  height: 50,
  width: '100%',
  margin: theme.spacing(1, 0),
  paddingRight: i18n.dir() === 'ltr' ? (isMobileDevice() ? 4 : 16) : undefined,
  paddingLeft: i18n.dir() === 'ltr' ? undefined : isMobileDevice() ? 4 : 16
}))

export const TimeColumn = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row',
  height: '100%',
  minWidth: 90,
  maxWidth: 90,
  fontSize: 14,
  padding: theme.spacing(0, 2)
}))

export const TimeContainer = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  height: '100%',
  width: '100%'
}))

export const TimeLabel = styled('div')(({ theme }) => ({
  width: '100%',
  textAlign: i18n.dir() === 'ltr' ? 'end' : 'start'
}))

export const NoItems = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  color: theme.palette.text.disabled,
  textAlign: 'center',
  width: '100%',
  padding: 16,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
}))

export const TodayButton = styled(Box)<{
  isCondensedWidth?: boolean
  isIconMode?: boolean
}>(({ theme, isCondensedWidth, isIconMode }) => ({
  background: theme.palette.button.background.primary,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  fontFamily: 'inherit',
  justifyItems: 'center',
  alignItems: 'center',
  borderRadius: '68px',
  fontSize: '19px',
  height: '52px',
  lineHeight: '51px',
  width: 'auto',
  padding: '0 4px',
  border: 'none',
  boxShadow: 'none',
  ...(!isCondensedWidth &&
    !isIconMode && {
      width: '125px',
      border: `1px solid ${theme.palette.border.primary}`,
      boxShadow: '1px 1px 6px rgba(50, 50, 71, 0.05)'
    }),
  ...(isIconMode && {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    width: '30px',
    height: '30px',
    padding: 0
  }),
  '@media (max-height: 500px)': {
    height: '30px !important',
    width: '30px !important',
    marginTop: '4px',
    border: 'none',
    boxShadow: 'none'
  },
  '&:hover': {
    background: theme.palette.surface.hover
  },
  '&:active': {
    background: theme.palette.surface.hover
  },
  '& .today-text': {
    color: theme.palette.text.primary,
    display: 'none',
    fontWeight: 400,
    ...(!isCondensedWidth && {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      flex: 1
    }),
    '@media (max-height: 500px)': {
      display: 'none'
    }
  },
  '& .today-image': {
    '@media (max-height: 500px)': {
      display: 'flex'
    }
  }
}))
